<template>
  <section class="resource-management" :data-resource-type="resourceType" lang="zh-TW">
    <h2>{{ title }}</h2>
    <p v-if="error" role="alert" data-testid="resource-error">{{ error }}</p>
    <p v-if="message" role="status">{{ message }}</p>
    <template v-if="!editing">
      <button type="button" @click="openNew" data-testid="resource-create">新增{{ singular }}</button>
      <p v-if="loading">載入資料中…</p>
      <table v-else class="resource-table">
        <thead><tr><th>編號</th><th>{{ singular }}</th><th v-if="isArticleTaxonomy">狀態</th><th>操作</th></tr></thead>
        <tbody><tr v-for="item in items" :key="item.id" :data-resource-id="item.id">
          <td>{{ item.id }}</td><td>{{ item.name || plainText(item.description).slice(0, 80) || '未命名模板' }}</td>
          <td v-if="isArticleTaxonomy">{{ item.enabled ? '啟用' : '停用' }}</td>
          <td><button type="button" @click="openExisting(item)" data-testid="resource-edit">編輯</button><button type="button" @click="remove(item)" data-testid="resource-delete">刪除</button></td>
        </tr></tbody>
      </table>
      <p v-if="!loading && !items.length">目前沒有資料。</p>
      <div v-if="pageable" class="actions"><button @click="changePage(-1)" :disabled="page === 0 || loading">上一頁</button><span>第 {{ page + 1 }} ／ {{ Math.max(totalPages, 1) }} 頁</span><button @click="changePage(1)" :disabled="page + 1 >= totalPages || loading">下一頁</button></div>
    </template>
    <template v-else>
      <h3>{{ source.id ? '編輯' : '新增' }}{{ singular }}{{ source.id ? `（編號 ${source.id}）` : '' }}</h3>
      <BilingualContentTabs ref="contentTabs" :resource-type="resourceType" :resource-id="source.id" :source-revision="sourceRevision">
        <form @submit.prevent="save" class="source-form">
          <div class="field" v-if="resourceType !== 'template'"><label for="source-name">{{ singular }}名稱（中文）</label><input id="source-name" data-testid="source-name" v-model="source.name" required /></div>
          <div class="field" v-if="resourceType === 'template'"><label for="source-description">模板介紹（中文）</label><LocalRichTextEditor id="source-description" v-model="source.description" :init="editorOptions" /></div>
          <template v-if="isArticleTaxonomy">
            <div class="field"><label for="source-description">描述（中文，選填）</label><textarea id="source-description" v-model="source.description" /></div>
            <div class="field"><label for="source-color">顯示顏色（共用）</label><input id="source-color" type="color" v-model="source.color" /></div>
            <div class="field"><label for="source-enabled"><input id="source-enabled" type="checkbox" v-model="source.enabled" /> 啟用（共用）</label></div>
          </template>
          <p>中文內容與英文內容分開儲存；英文操作不更動圖片、顏色、啟用狀態及關聯。</p>
          <button type="submit" :disabled="saving" data-testid="source-save">{{ saving ? '儲存中…' : '儲存中文內容' }}</button>
        </form>
      </BilingualContentTabs>
      <button type="button" @click="back" data-testid="resource-back">返回列表</button>
    </template>
  </section>
</template>
<script setup>
/* global defineProps, defineExpose */
import { computed, onMounted, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import Swal from '@/utils/chineseSwal';
import BilingualContentTabs from './BilingualContentTabs.vue';
import LocalRichTextEditor from './LocalRichTextEditor.vue';
import * as category from '@/api/category.api';
import * as templates from '@/api/productDescriptionTemplate.api';
import * as articleCategories from '@/api/articleCategory.api';
import * as articleTags from '@/api/articleTag.api';
import { uploadProductRichtextImage } from '@/api/file.api';
import { guardBrowserClose } from '@/utils/unsavedChanges';

const props = defineProps({ resourceType: { type: String, required: true } });
const config = computed(() => ({
  category: { singular: '商品分類', list: category.getAllCategories, create: category.createCategory, update: category.updateCategory, delete: category.deleteCategory, get: category.getCategoryById },
  template: { singular: '商品模板', list: templates.getAllTemplates, create: templates.createTemplate, update: templates.updateTemplate, delete: templates.deleteTemplate, get: templates.getTemplateById },
  'article-category': { singular: '文章分類', list: articleCategories.getArticleCategoryPage, create: articleCategories.createArticleCategory, update: articleCategories.updateArticleCategory, delete: articleCategories.deleteArticleCategory, get: articleCategories.getArticleCategoryById },
  'article-tag': { singular: '文章標籤', list: articleTags.getArticleTagPage, create: articleTags.createArticleTag, update: articleTags.updateArticleTag, delete: articleTags.deleteArticleTag, get: articleTags.getArticleTagById }
})[props.resourceType]);
const singular = computed(() => config.value.singular);
const title = computed(() => `${singular.value}管理`);
const isArticleTaxonomy = computed(() => ['article-category', 'article-tag'].includes(props.resourceType));
const pageable = isArticleTaxonomy;
const items = ref([]);
const page = ref(0);
const totalPages = ref(0);
const source = ref({});
const sourceBaseline = ref('');
const sourceRevision = ref(0);
const editing = ref(false);
const loading = ref(false);
const saving = ref(false);
const error = ref('');
const message = ref('');
const contentTabs = ref(null);
const editorOptions = { height: 400, images_upload_handler: async blobInfo => {
  try { const result = await uploadProductRichtextImage(blobInfo.blob()); if (result.url) return result.url; } catch { /* display a Chinese error */ }
  throw new Error('圖片上傳失敗，請檢查檔案或服務後重試。');
} };
function plainText(html) { return new DOMParser().parseFromString(html || '', 'text/html').body.textContent || ''; }
function reportError(cause) {
  const status = cause.response?.status;
  error.value = { 401: '登入已失效，請重新登入。', 403: '此操作需要管理員權限。', 404: '找不到此資源，請重新載入列表。', 409: '資料版本或關聯已變動，請重新確認後再操作。', 422: '內容驗證失敗，請檢查名稱及介紹。' }[status] || '操作失敗，請檢查欄位與關聯；使用中的分類或模板可能無法刪除。';
}
async function load() {
  loading.value = true;
  try { const result = pageable.value ? await config.value.list(page.value, 20, 'id,desc') : await config.value.list(); items.value = Array.isArray(result) ? result : result.content || []; totalPages.value = result.totalPages || 0; }
  catch (cause) { reportError(cause); }
  finally { loading.value = false; }
}
function openNew() { source.value = { name: '', description: '', color: '#3498db', enabled: true }; sourceBaseline.value = JSON.stringify(source.value); editing.value = true; error.value = ''; message.value = ''; }
async function openExisting(item) {
  error.value = ''; message.value = '';
  try { source.value = { ...(await config.value.get(item.id)) }; sourceBaseline.value = JSON.stringify(source.value); editing.value = true; }
  catch (cause) { reportError(cause); }
}
async function save() {
  saving.value = true; error.value = ''; message.value = '';
  try {
    const payload = props.resourceType === 'category' ? { name: source.value.name } : props.resourceType === 'template' ? { description: source.value.description } : { name: source.value.name, description: source.value.description, color: source.value.color, enabled: !!source.value.enabled };
    const result = source.value.id ? await config.value.update(source.value.id, payload) : await config.value.create(payload);
    source.value = { ...source.value, ...result };
    if (!source.value.id) throw new Error('Missing resource id');
    sourceBaseline.value = JSON.stringify(source.value); sourceRevision.value++; message.value = '中文內容已儲存，可以繼續維護英文內容。';
  } catch (cause) { reportError(cause); }
  finally { saving.value = false; }
}
function confirmSourceDiscard() { return !editing.value || JSON.stringify(source.value) === sourceBaseline.value || window.confirm('中文內容尚未儲存，確定要捨棄修改嗎？'); }
function confirmDiscard() { return confirmSourceDiscard() && (!contentTabs.value || contentTabs.value.confirmDiscard()); }
async function back() { if (!confirmDiscard()) return; editing.value = false; source.value = {}; error.value = ''; await load(); }
async function remove(item) {
  const result = await Swal.fire({ title: `刪除${singular.value}`, text: `確定刪除編號 ${item.id} 嗎？此操作會一併刪除其翻譯資料。`, icon: 'warning', showCancelButton: true, confirmButtonText: '確定刪除', cancelButtonText: '取消' });
  if (!result.isConfirmed) return;
  try { await config.value.delete(item.id); message.value = '資料已刪除。'; error.value = ''; await load(); }
  catch (cause) { reportError(cause); }
}
async function changePage(delta) { page.value += delta; await load(); }
onMounted(load);
onBeforeRouteLeave(() => confirmSourceDiscard());
guardBrowserClose(() => editing.value && JSON.stringify(source.value) !== sourceBaseline.value);
watch(() => props.resourceType, () => { editing.value = false; source.value = {}; page.value = 0; load(); });
defineExpose({ confirmDiscard });
</script>
<style scoped>
.resource-management { padding: 24px; color: #ddd; }.resource-management h2,.resource-management h3 { margin: 12px 0 24px; }.resource-management p { margin: 12px 0; }[role="alert"] { color: #ff9898; }button { padding: 10px 16px; margin: 4px; background: #383847; color: #eee; border: 1px solid #777; border-radius: 4px; cursor: pointer; }button:disabled { opacity: .5; cursor: default; }.resource-table { width: 100%; margin: 20px 0; border-collapse: collapse; }.resource-table th,.resource-table td { padding: 12px; border-bottom: 1px solid #666; text-align: left; }.source-form { background: #20202b; padding: 24px; border: 1px solid #555; border-radius: 6px; }.field { margin: 18px 0; }.field label { display: block; margin-bottom: 8px; }.field input:not([type="checkbox"]):not([type="color"]),.field textarea { width: 100%; padding: 10px; background: #15151f; color: #eee; border: 1px solid #777; border-radius: 4px; }.actions { display: flex; align-items: center; gap: 12px; }
</style>
